import React, { useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import Modal from '../../components/common/Modal.jsx';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { formatMoney, formatDate, primero, useLocalTable } from '../finanzas-cxp/shared.js';

/**
 * Base compartida de `ReporteContenedoresPage` y `ReporteContenedoresCargosPage`
 * (V1: `IbuFront/src/components/views/Reportes/{ReporteContenedores,
 * ReporteContenedoresCargos}.jsx`). Ambas pantallas de V1 son, en los datos que
 * muestran, EXACTAMENTE la misma tabla (Numero/Fecha/Referencia/Contenedor/Cliente/
 * Naviera/Cantidad/ETA Real/Cargos/Gastos/Utilidad + botón "$" de desgloce) —
 * la única diferencia real es la fuente: `ReporteContenedores` lista TODOS los
 * contenedores del rango de fechas (`GET /contenedores/fechas/:inicio/:fin`),
 * `ReporteContenedoresCargos` solo los que ya tienen cargos (`GET
 * /contenedores/fechas-cargos/:inicio/:fin`, filtro `totalCargos > 0` en el
 * backend). V1 llegaba al mismo resultado para la segunda pantalla consultando
 * `CargosContenedor` y agrupando por contenedor en el cliente (deduplicando) — acá
 * no hace falta: el backend V2 ya expone el contenedor deduplicado directamente
 * (`contenedores.service.js#listarPorFechasConCargos`), así que no se necesita
 * ningún endpoint nuevo de agregación para esta tarea.
 *
 * Ambas pantallas de V1 están gateadas por el mismo flag `user.contenedoresReporte`
 * -> `contenedores:reporte` en V2 (ver `permisos.map.js`), por eso las dos páginas
 * concretas pasan el mismo `permiso`.
 *
 * `totalUtilidad` no es un campo propio de `Contenedores` — V1 lo calcula en el
 * cliente como `totalCargos - totalGastos` (ver `ReporteContenedores.jsx` línea
 * ~123); se replica igual aquí.
 */
export default function ReporteContenedoresBase({ endpoint, title, permiso, modo = 'contenedores' }) {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(permiso);

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 91);
  const [fechaInicio, setFechaInicio] = useState(inicio.toISOString().slice(0, 10));
  const [fechaFin, setFechaFin] = useState(hoy.toISOString().slice(0, 10));

  const [navieras, setNavieras] = useState([]);
  const [selectedNaviera, setSelectedNaviera] = useState('');

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const [mailOpen, setMailOpen] = useState(false);
  const [desgloce, setDesgloce] = useState(null); // { numero, totalCargos, totalGastos, cargos:[], gastos:[] }

  useEffect(() => {
    if (!puedeVer) return;
    apiClient.get('/proveedores', { params: { tipo: 'Naviera', limit: 500 } }).then((r) => setNavieras(r.data.data || [])).catch(() => {});
  }, [puedeVer]);

  async function cargar() {
    if (!puedeVer || !fechaInicio || !fechaFin) return;
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`${endpoint}/${fechaInicio}/${fechaFin}`);
      const crudo = data.data || [];
      // Fila de la tabla a partir de un contenedor + el cliente a mostrar (V1: `nombre_comercial-idContenedor`).
      const armarFila = (c, cliente) => {
        const naviera = primero(c.navieras);
        const totalCargos = Number(c.totalCargos || 0);
        const totalGastos = Number(c.totalGastos || 0);
        return {
          _id: c._id,
          numero: c.idContenedor,
          fecha: c.fecha,
          observaciones: c.observaciones || '',
          totalGeneral: Number(c.totalGeneral || 0),
          numeroInterno: `${cliente?.nombre_comercial || ''}-${c.idContenedor}`,
          contenedor: c.contenedor || '',
          cliente: cliente?.nombre_comercial || '',
          idCliente: cliente?._id || '',
          naviera: naviera?.name || '',
          idNaviera: naviera?._id || '',
          totalCantidad: c.totalCantidad || 0,
          etaReal: c.etaReal,
          totalCargos,
          totalGastos,
          totalUtilidad: totalCargos - totalGastos,
        };
      };
      let lista;
      if (modo === 'cargos') {
        // V1 (ReporteContenedoresCargos): consulta los CargosContenedor del rango
        // (`cargosContenedorFechas`), ordena por `idCargoContenedor` desc y agrupa por
        // contenedor (deduplica) conservando el orden de primera aparición.
        const vistos = new Map();
        [...crudo]
          .sort((a, b) => (a.idCargoContenedor < b.idCargoContenedor ? 1 : -1))
          .forEach((cargo) => {
            const cont = primero(cargo.contenedores);
            if (!cont || !cont._id || vistos.has(cont._id)) return;
            vistos.set(cont._id, armarFila(cont, primero(cargo.clientes)));
          });
        lista = [...vistos.values()];
      } else {
        lista = [...crudo]
          .sort((a, b) => (a.idContenedor < b.idContenedor ? 1 : -1))
          .map((c) => armarFila(c, primero(c.clientes)));
      }
      setFilas(lista);
    } catch (e) {
      setError(e.message);
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [puedeVer, fechaInicio, fechaFin, endpoint]);

  const filasFiltradas = useMemo(() => {
    let r = filas;
    if (selectedNaviera) r = r.filter((f) => f.idNaviera === selectedNaviera);
    return r;
  }, [filas, selectedNaviera]);

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(filasFiltradas, {
    // V1: numero, cliente, contenedor, naviera, cantidad, gastos, cargos, utilidad.
    searchFields: ['numero', 'cliente', 'contenedor', 'naviera', 'totalCantidad', 'totalGastos', 'totalCargos', 'totalUtilidad'],
    perPage: 50,
  });

  const totalUtilidad = filtradas.reduce((t, f) => t + f.totalUtilidad, 0);

  async function verDesgloce(row) {
    setDesgloce({ numero: row.contenedor, totalCargos: row.totalCargos, totalGastos: row.totalGastos, cargos: [], gastos: [] });
    try {
      const [cargosRes, gastosRes] = await Promise.all([
        apiClient.get(`/cargos-contenedor/contenedor/${row._id}`).catch(() => ({ data: { data: [] } })),
        apiClient.get(`/gastos-contenedor/contenedor/${row._id}`).catch(() => ({ data: { data: [] } })),
      ]);
      setDesgloce((d) => (d ? {
        ...d,
        cargos: cargosRes.data.data || [],
        gastos: gastosRes.data.data || [],
      } : d));
    } catch (e) {
      // silencioso — el modal ya muestra los totales del contenedor aunque el desgloce falle
    }
  }

  const columnas = [
    { key: 'numero', label: 'Número', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'numeroInterno', label: 'No. Referencia', sortable: true },
    { key: 'contenedor', label: 'Contenedor', sortable: true },
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'naviera', label: 'Naviera', sortable: true },
    { key: 'totalCantidad', label: 'Cantidad', sortable: true, className: 'text-right', render: (r) => new Intl.NumberFormat('en-US').format(r.totalCantidad) },
    { key: 'etaReal', label: 'ETA Real', sortable: true },
    { key: 'totalCargos', label: 'Cargos', sortable: true, className: 'text-right', render: (r) => formatMoney(r.totalCargos) },
    { key: 'totalGastos', label: 'Gastos', sortable: true, className: 'text-right', render: (r) => formatMoney(r.totalGastos) },
    { key: 'totalUtilidad', label: 'Utilidad', sortable: true, className: 'text-right', render: (r) => formatMoney(r.totalUtilidad) },
    {
      key: '__desgloce', label: 'Desgloce', sortable: false, className: 'text-center',
      render: (row) => (
        <button className="btn-primary text-xs px-2 py-1" onClick={() => verDesgloce(row)}>$</button>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  // Exportación (V1: PDFTabla()/excel() -- solo PDF y Excel). Columnas EXACTAS de V1:
  //   Reporte Contenedores:             Numero, Fecha, Naviera, Cliente, Observaciones, Cantidad, Total
  //   Reporte Contenedores con Cargos:  Numero, Fecha, Naviera, Cliente, Observaciones, Cargos, Gastos, Utilidad
  const columnasExport = modo === 'cargos'
    ? [
        { key: 'numero', label: 'Numero' },
        { key: 'fecha', label: 'Fecha' },
        { key: 'naviera', label: 'Naviera' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'observaciones', label: 'Observaciones' },
        { key: 'totalCargos', label: 'Cargos', render: (r) => formatMoney(r.totalCargos) },
        { key: 'totalGastos', label: 'Gastos', render: (r) => formatMoney(r.totalGastos) },
        { key: 'totalUtilidad', label: 'Utilidad', render: (r) => formatMoney(r.totalUtilidad) },
      ]
    : [
        { key: 'numero', label: 'Numero' },
        { key: 'fecha', label: 'Fecha' },
        { key: 'naviera', label: 'Naviera' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'observaciones', label: 'Observaciones' },
        { key: 'totalCantidad', label: 'Cantidad' },
        { key: 'totalGeneral', label: 'Total', render: (r) => formatMoney(r.totalGeneral) },
      ];
  // El Excel lleva los importes como número (V1), no como texto con formato.
  const columnasExcel = columnasExport.map((c) => ({ key: c.key, label: c.label }));

  const opcionesPdf = { subtitulo: `${fechaInicio} - ${fechaFin}`, fileName: 'Contenedores' };
  const hacerPdf = () => exportTablePdf('Listado Contenedores', columnasExport, filasFiltradas, opcionesPdf);
  const hacerExcel = () => exportTableExcel('ReporteContenedores', columnasExcel, filasFiltradas, {
    fileName: 'ReporteContenedores', sheetName: 'ReporteContenedores',
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={hacerPdf} className="btn-outline text-xs px-3 py-1.5">PDF</button>
          <button type="button" onClick={() => setMailOpen(true)} className="btn-outline text-xs px-3 py-1.5">eMail</button>
          <button type="button" onClick={hacerExcel} className="btn-outline text-xs px-3 py-1.5">Excel</button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="input-base" />
        </div>
        <div>
          <label className="label">Navieras</label>
          <select value={selectedNaviera} onChange={(e) => { setSelectedNaviera(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {[...navieras].sort((a, b) => (a.name > b.name ? 1 : -1)).map((n) => (
              <option key={n._id} value={n._id}>{n.name}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" className="input-base w-60" />
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <DataTable
        columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ totalGastos: 'Total', totalUtilidad: formatMoney(totalUtilidad) }}
      />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado de Contenedores"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Listado Contenedores', columnasExport, filasFiltradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Listado Contenedores', email, fileName: 'ReporteContenedores.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras el listado de Contenedores.'),
          });
        }}
      />

      <Modal open={!!desgloce} onClose={() => setDesgloce(null)} title={`Desgloce Contenedor ${desgloce?.numero || ''}`} width="max-w-md">
        {desgloce && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-1">CARGOS</p>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500"><th>Fecha</th><th>Concepto</th><th className="text-right">Importe</th></tr></thead>
                <tbody>
                  {desgloce.cargos.map((c) => (
                    <tr key={c._id}><td>{formatDate(c.fecha)}</td><td>{primero(c.conceptosGastos)?.name || '—'}</td><td className="text-right">{formatMoney(c.total)}</td></tr>
                  ))}
                  <tr className="font-semibold border-t border-slate-200"><td></td><td>Total</td><td className="text-right">{formatMoney(desgloce.totalCargos)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-1">GASTOS</p>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500"><th>Fecha</th><th>Concepto</th><th className="text-right">Importe</th></tr></thead>
                <tbody>
                  {desgloce.gastos.map((g) => (
                    <tr key={g._id}><td>{formatDate(g.fecha)}</td><td>{primero(g.conceptosGastos)?.name || '—'}</td><td className="text-right">{formatMoney(g.total)}</td></tr>
                  ))}
                  <tr className="font-semibold border-t border-slate-200"><td></td><td>Total</td><td className="text-right">{formatMoney(desgloce.totalGastos)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
